import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem } from './fitness/fitnessApi';

const LIST = 'UserProfiles';

// Single source of truth for user prefs: the SharePoint column each pref maps
// to, plus how to read that column back into app state. The load path and
// updatePref both drive off this one constant.
//
// This used to be two separate literals — an inline object in loadProfile and
// another inside updatePref — which could drift apart silently: a pref present
// in the read shape but missing from the write map would appear to save (the
// optimistic setPrefs ran) and then vanish on the next load. One map, no drift.
const COL_MAP = {
  briefingCollapsed: { col: 'BriefingCollapsed', read: v => (v !== undefined ? v : true) },
  readingsCollapsed: { col: 'ReadingsCollapsed', read: v => v || false },
  // Text column: undefined/''/null all collapse to a single null sentinel
  // meaning "rotate with the calendar", so consumers can test truthiness.
  themeOverride: { col: 'ThemeOverride', read: v => v || null },
  darkMode: { col: 'DarkMode', read: v => v || false },
};

const UserProfileContext = createContext({
  nickname: '',
  loading: true,
  profileId: null,
  showPrompt: false,
  prefs: {},
  updateNickname: async () => {},
  updatePref: async () => {},
});

export function useUserProfile() {
  return useContext(UserProfileContext);
}

export function UserProfileProvider({ children, getToken, userEmail }) {
  const [nickname, setNickname] = useState('');
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrompt, setShowPrompt] = useState(false);
  const [prefs, setPrefs] = useState({});

  const loadProfile = useCallback(async () => {
    if (!userEmail || !getToken) {
      setLoading(false);
      return;
    }
    try {
      const token = await getToken();
      const items = await getItems(token, LIST);
      const profile = items.find(
        it => (it.UserEmail || '').toLowerCase() === userEmail.toLowerCase()
      );
      if (profile) {
        setNickname(profile.Nickname || '');
        setProfileId(profile.id);
        setPrefs(Object.fromEntries(
          Object.entries(COL_MAP).map(([key, def]) => [key, def.read(profile[def.col])])
        ));
      } else {
        setShowPrompt(true);
      }
    } catch (err) {
      console.warn('Failed to load user profile:', err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const updateNickname = useCallback(async (newNickname) => {
    if (!newNickname.trim()) return;
    try {
      const token = await getToken();
      if (profileId) {
        await updateItem(token, LIST, profileId, { Nickname: newNickname.trim() });
      } else {
        const created = await createItem(token, LIST, {
          Title: newNickname.trim(),
          UserEmail: userEmail,
          Nickname: newNickname.trim(),
          CreatedDate: new Date().toISOString(),
        });
        setProfileId(created.id);
      }
      setNickname(newNickname.trim());
      setShowPrompt(false);
    } catch (err) {
      console.error('Failed to save nickname:', err.message);
      throw err;
    }
  }, [getToken, userEmail, profileId]);

  const updatePref = useCallback(async (key, value) => {
    // Optimistic update
    setPrefs(prev => ({ ...prev, [key]: value }));
    if (!profileId) return;
    try {
      const token = await getToken();
      const def = COL_MAP[key];
      if (!def) {
        // Loud, not silent — an unmapped key used to no-op with no trace.
        console.warn(`updatePref: no column mapped for "${key}" — not persisted`);
        return;
      }
      await updateItem(token, LIST, profileId, { [def.col]: value });
    } catch {
      // Pref save failure is not critical
    }
  }, [getToken, profileId]);

  return (
    <UserProfileContext.Provider value={{ nickname, loading, profileId, showPrompt, prefs, updateNickname, updatePref }}>
      {children}
    </UserProfileContext.Provider>
  );
}

// Lookup helper — resolve an email to a nickname from the full profiles list
let cachedProfiles = null;
let cacheToken = null;

export async function getNicknameForEmail(token, email) {
  if (!cachedProfiles || cacheToken !== token) {
    try {
      cachedProfiles = await getItems(token, LIST);
      cacheToken = token;
    } catch {
      return email;
    }
  }
  const profile = cachedProfiles.find(
    p => (p.UserEmail || '').toLowerCase() === (email || '').toLowerCase()
  );
  return profile?.Nickname || email;
}

export async function getAllProfiles(token) {
  if (!cachedProfiles || cacheToken !== token) {
    try {
      cachedProfiles = await getItems(token, LIST);
      cacheToken = token;
    } catch {
      return [];
    }
  }
  return cachedProfiles;
}
